// The Documents control in the office header (Session DOC1, Director, 24 October 2026).
//
// The Schedule of Fees is one of ELE's most used documents and was buried in Settings, Your account, Documents; the
// subcontractor payment schedule (the company standard rate card) had no download at all. This puts both one tap away from
// every office page: an icon button beside the header's other controls that opens a short menu, and each item opens its PDF
// in a new tab. No new nav item and no new page (house rule 11).
//
// THE SUBCONTRACTOR RATE CARD IS ADMIN ONLY and is not rendered for the office role at all (the AUU rule: the office role
// does not read the money). Hiding it is the courtesy; the server's route is the guard and answers 403 to an office login.
// The Settings, Documents card stays as it was.
//
// SESSION RCS (Director, 27 October 2026: "those buttons should be instantly able to fire out to anyone"): two more items.
// "Copy Schedule of Fees link" copies the PUBLIC Schedule of Fees address (the same document customers are sent, open to
// anyone), for office and admin. "Share rate card..." (ADMIN ONLY) opens RateCardShareModal: a private link for one named
// person, 7 days, switch-off. The rate card itself is never given a public address.
import { useState } from "react";
import { FileText } from "lucide-react";
import { useSingleTimeout } from "../lib/useSingleTimeout";
import { PUBLIC_SCHEDULE_OF_FEES_PATH, SCHEDULE_OF_FEES_HREF, SUBCONTRACTOR_RATE_CARD_HREF } from "../lib/documentHrefs";
import { OverflowMenu, overflowItemCls } from "./OverflowMenu";
import { RateCardShareModal } from "./RateCardShareModal";

/** The public Schedule of Fees address, absolute from the page's own origin. */
export function publicScheduleOfFeesUrl(): string {
  return `${window.location.origin}${PUBLIC_SCHEDULE_OF_FEES_PATH}`;
}

type CopyState = { ok: true } | { ok: false; url: string } | null;

// The note clears itself after six seconds through useSingleTimeout, which cancels the timer on unmount and on the next copy
// (a bare setTimeout outlived the component and failed confirmationTimers.test.ts).
function useCopyFeesLink(): [CopyState, () => void] {
  const [state, setState] = useState<CopyState>(null);
  const later = useSingleTimeout();
  const copy = () => {
    const url = publicScheduleOfFeesUrl();
    navigator.clipboard
      .writeText(url)
      .then(() => setState({ ok: true }))
      .catch(() => setState({ ok: false, url }));
    later(() => setState(null), 6000);
  };
  return [state, copy];
}

function CopyNote({ state }: { state: CopyState }) {
  if (!state) return null;
  return (
    <span role="status" className="text-xs text-ele-body">
      {state.ok ? "Schedule of Fees link copied." : `Could not copy. The link is ${state.url}`}
    </span>
  );
}

// WHY THE ICON IS HIDDEN BELOW 640px. The header already holds the hamburger, the logo (103px at its 26px height), search, the
// notification opt-in and the theme toggle, each a 44px target; a fifth does not fit a 320 to 390px phone without clipping the
// header (the office <main> clips, so it would be cut off, not scrolled). On a phone the same two links sit in the
// navigation drawer, above the identity block, instead (DocumentsDrawerLinks), so they are still two taps from any page: the hamburger, then the document.
//
// The two link rows take overflowItemCls, whose 44px floor holds up to 768px (it is shown from 640px, so a phone held sideways sees it).

export function DocumentsMenu({ role }: { role?: string | undefined }) {
  const isAdmin = role === "admin";
  const [sharing, setSharing] = useState(false);
  const [copyState, copyFees] = useCopyFeesLink();
  return (
    <div className="hidden flex-none items-center gap-2 sm:flex">
    <OverflowMenu
      label="Documents"
      closeOnSelect
      triggerClassName="flex h-11 w-11 flex-none items-center justify-center rounded-ele text-ele-muted outline-none transition hover:text-ele-strong focus-visible:ring-2 focus-visible:ring-ele-purple"
      triggerContent={<FileText aria-hidden size={20} />}
    >
      <a role="menuitem" href={SCHEDULE_OF_FEES_HREF} target="_blank" rel="noopener noreferrer" className={overflowItemCls}>
        Schedule of Fees (PDF)
      </a>
      <button type="button" role="menuitem" onClick={copyFees} className={overflowItemCls}>
        Copy Schedule of Fees link
      </button>
      {isAdmin ? (
        <a role="menuitem" href={SUBCONTRACTOR_RATE_CARD_HREF} target="_blank" rel="noopener noreferrer" className={overflowItemCls}>
          Subcontractor rate card (PDF)
        </a>
      ) : null}
      {isAdmin ? (
        <button type="button" role="menuitem" onClick={() => setSharing(true)} className={overflowItemCls}>
          Share rate card...
        </button>
      ) : null}
    </OverflowMenu>
    <CopyNote state={copyState} />
    {sharing && isAdmin ? <RateCardShareModal onClose={() => setSharing(false)} /> : null}
    </div>
  );
}

/** The same items as plain 44px rows, for the phone's navigation drawer and the foot of the desktop sidebar (Director,
 * 27 October 2026: the documents sit in the same place on the PC as on the phone). The office sees the Schedule of Fees
 * and its link only. These are links to documents, not menu items, so the 14-item menu count is unchanged. */
export function DocumentsDrawerLinks({ role, testId = "drawer-documents" }: { role?: string | undefined; testId?: string }) {
  const [sharing, setSharing] = useState(false);
  const [copyState, copyFees] = useCopyFeesLink();
  const linkCls = "flex min-h-11 items-center rounded-ele px-3 font-menu text-sm tracking-wide text-ele-body transition hover:text-ele-strong";
  return (
    <div className="border-t border-ele-purple-accent px-2 py-2" data-testid={testId}>
      <div className="px-3 pb-1 font-menu text-xs uppercase tracking-wide text-ele-muted">Documents</div>
      <a href={SCHEDULE_OF_FEES_HREF} target="_blank" rel="noopener noreferrer" className={linkCls}>
        Schedule of Fees (PDF)
      </a>
      <button type="button" onClick={copyFees} className={`${linkCls} w-full text-left`}>
        Copy Schedule of Fees link
      </button>
      {role === "admin" ? (
        <a href={SUBCONTRACTOR_RATE_CARD_HREF} target="_blank" rel="noopener noreferrer" className={linkCls}>
          Subcontractor rate card (PDF)
        </a>
      ) : null}
      {role === "admin" ? (
        <button type="button" onClick={() => setSharing(true)} className={`${linkCls} w-full text-left`}>
          Share rate card...
        </button>
      ) : null}
      <div className="px-3">
        <CopyNote state={copyState} />
      </div>
      {sharing && role === "admin" ? <RateCardShareModal onClose={() => setSharing(false)} /> : null}
    </div>
  );
}
